import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  List,
  Code,
  ChevronDown,
  Type,
  Hash,
  User,
  Calendar,
  MapPin,
  Smartphone,
  Plus,
  X,
} from "lucide-react";

// Mock placeholder options - replace with your actual constants
const placeholderOptions = [
  { value: "{{user.name}}", label: "User Name", icon: User, category: "User" },
  {
    value: "{{user.email}}",
    label: "User Email",
    icon: User,
    category: "User",
  },
  {
    value: "{{user.phone}}",
    label: "User Phone",
    icon: Smartphone,
    category: "User",
  },
  { value: "{{app.name}}", label: "App Name", icon: Type, category: "App" },
  { value: "{{order.id}}", label: "Order ID", icon: Hash, category: "Order" },
  {
    value: "{{order.total}}",
    label: "Order Total",
    icon: Hash,
    category: "Order",
  },
  {
    value: "{{location.name}}",
    label: "Location",
    icon: MapPin,
    category: "Location",
  },
  {
    value: "{{event.date}}",
    label: "Event Date",
    icon: Calendar,
    category: "Event",
  },
  {
    value: "{{event.time}}",
    label: "Event Time",
    icon: Calendar,
    category: "Event",
  },
];

const RichTextEditor = ({ value, onChange, placeholder, maxLength, error }) => {
  const [showVariables, setShowVariables] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        blockquote: false,
        codeBlock: false,
      }),
      Placeholder.configure({
        placeholder: placeholder || "Write your notification content...",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[120px] p-4",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const insertVariable = (variable) => {
    if (editor) {
      editor.chain().focus().insertContent(variable).run();
    }
    setShowVariables(false);
    setSearchTerm("");
  };

  const filteredVariables = placeholderOptions.filter(
    (option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedVariables = filteredVariables.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {});

  const MenuBar = () => {
    if (!editor) return null;

    return (
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        {/* Formatting Controls */}
        <div className="flex items-center gap-1">
          <div className="flex items-center bg-white rounded-lg border shadow-sm">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 hover:bg-gray-100 transition-colors rounded-l-lg ${
                editor.isActive("bold")
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600"
              }`}
              title="Bold (Ctrl+B)"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 hover:bg-gray-100 transition-colors border-l border-gray-200 ${
                editor.isActive("italic")
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600"
              }`}
              title="Italic (Ctrl+I)"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 hover:bg-gray-100 transition-colors border-l border-gray-200 ${
                editor.isActive("bulletList")
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600"
              }`}
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={`p-2 hover:bg-gray-100 transition-colors border-l border-gray-200 rounded-r-lg ${
                editor.isActive("code")
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600"
              }`}
              title="Inline Code"
            >
              <Code className="w-4 h-4" />
            </button>
          </div>

          {/* Variables Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVariables(!showVariables)}
              className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-gray-700"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Insert Variable</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  showVariables ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>

        {/* Character Count */}
        {maxLength && (
          <div className="text-sm text-gray-500">
            <span
              className={
                editor?.storage?.characterCount?.characters() > maxLength
                  ? "text-red-500"
                  : ""
              }
            >
              {editor?.storage?.characterCount?.characters() || 0}
            </span>
            <span> / {maxLength}</span>
          </div>
        )}
      </div>
    );
  };

  // Close dropdown when clicking outside or pressing escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showVariables) {
        setShowVariables(false);
      }
    };

    const handleClickOutside = (event) => {
      if (showVariables && !event.target.closest(".relative")) {
        setShowVariables(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showVariables]);

  return (
    <div className="space-y-2">
      <div
        className={`border-2 rounded-xl overflow-hidden bg-white transition-colors ${
          error
            ? "border-red-300"
            : "border-gray-200 focus-within:border-blue-500"
        }`}
      >
        <MenuBar />

        <div className="relative">
          <EditorContent
            editor={editor}
            className="prose prose-sm max-w-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:p-4 [&_.ProseMirror.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror.is-editor-empty:first-child::before]:float-left [&_.ProseMirror.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror.is-editor-empty:first-child::before]:h-0"
          />

          {/* Character limit warning */}
          {maxLength &&
            editor?.storage?.characterCount?.characters() > maxLength && (
              <div className="absolute bottom-2 right-2 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
                Exceeds limit by{" "}
                {editor.storage.characterCount.characters() - maxLength}
              </div>
            )}
        </div>
      </div>

      {/* Variables Dropdown Portal - Outside of main container to prevent clipping */}
      {showVariables && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setShowVariables(false)}
        >
          <div
            className="absolute bg-white border rounded-xl shadow-2xl w-80 max-h-min overflow-hidden"
            style={{
              top: "120px", // Adjust based on your layout
              left: "50%",
              transform: "translateX(-50%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search */}
            <div className="p-3 border-b bg-gray-50">
              <input
                type="text"
                placeholder="Search variables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Variables List */}
            <div className="max-h-72 overflow-y-auto">
              {Object.keys(groupedVariables).length > 0 ? (
                Object.entries(groupedVariables).map(
                  ([category, variables]) => (
                    <div key={category} className="p-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2 sticky top-0 bg-white">
                        {category}
                      </h4>
                      <div className="space-y-1">
                        {variables.map((option) => {
                          const IconComponent = option.icon;
                          return (
                            <button
                              key={option.value}
                              onClick={() => insertVariable(option.value)}
                              className="w-full flex items-center gap-3 p-3 hover:bg-blue-50 rounded-lg transition-colors text-left"
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <IconComponent className="w-4 h-4 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {option.label}
                                </div>
                                <div className="text-xs text-blue-600 font-mono">
                                  {option.value}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )
                )
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <Type className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No variables found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try a different search term
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3  border-t bg-gray-50 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {Object.values(groupedVariables).reduce(
                  (acc, vars) => acc + vars.length,
                  0
                )}{" "}
                variables available
              </span>
              <button
                type="button"
                onClick={() => setShowVariables(false)}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 bg-white border rounded hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}

      {/* Helper text */}
      <div className="flex items-start gap-2 text-xs text-gray-500">
        <div className="flex-shrink-0 w-4 h-4 mt-0.5 bg-blue-100 rounded flex items-center justify-center">
          <Type className="w-2.5 h-2.5 text-blue-600" />
        </div>
        <div>
          <p className="font-medium mb-1">
            Tips for writing great notifications:
          </p>
          <ul className="space-y-0.5 list-disc list-inside">
            <li>Keep your message clear and concise</li>
            <li>Use variables to personalize content</li>
            <li>Include a clear call-to-action</li>
            <li>Test with different variable values</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
